import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link as RouterLink, Outlet } from "react-router-dom";
import {
    Box,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    Divider,
    ListItemIcon,
    AppBar,
    Toolbar,
    Breadcrumbs,
    Link,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    Backdrop,
    alpha
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { signOut } from "supertokens-auth-react/recipe/session";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import GlobalNotificationBanner from "../components/GlobalNotificationBanner";
import ErrorBoundary from "../components/ErrorBoundary";
import WebSocketErrorHandler from "../components/WebSocketErrorHandler";
import ImpersonationBanner from "../components/ImpersonationBanner";
import UserService from "../../services/UserService";
import { BreadcrumbProvider, useBreadcrumbs } from "../../context/BreadcrumbContext";
import { useGlobalError } from "../../context/GlobalErrorContext";
import { useThemeContext } from "../../context/ThemeContext";
import UserAvatar from "../components/UserAvatar";
import SubscriptionBlockModal from "../components/SubscriptionBlockModal";
import { ReactComponent as Logo } from "../../assets/logo.svg";

function LayoutContent() {
    const { mode, toggleTheme } = useThemeContext();
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const location = useLocation();
    const { breadcrumbs } = useBreadcrumbs();
    const { showSuccess, showError } = useGlobalError();

    // Name Editing State
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isNameDialogOpen, setIsNameDialogOpen] = useState(false);
    const [isSettingUp, setIsSettingUp] = useState(false);

    useEffect(() => {
        if (location.pathname !== "/auth") {
            // Only fetch if we haven't already loaded the user
            if (!user) {
                setLoadingUser(true);
                UserService.getProfile()
                    .then(profile => {
                        if (!profile) {
                            throw new Error("No profile found");
                        }
                        setUser(profile);
                    })
                    .catch(async (err) => {
                        console.log("Could not fetch user profile, redirecting to auth:", err);
                        try {
                            await signOut();
                        } catch (signOutError) {
                            console.warn("Sign out during profile fetch failure failed:", signOutError);
                        }
                        window.location.href = "/auth";
                    })
                    .finally(() => {
                        setLoadingUser(false);
                    });
            }
        } else {
            setLoadingUser(false);
        }
    }, [location.pathname, navigate, user]);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    async function onLogout() {
        try {
            await signOut();
        } catch (err) {
            console.warn("Sign out failed:", err);
        }
        window.location.href = "/auth";
    }

    const handleAvatarChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const result = await UserService.uploadAvatar(file);
            setUser({ ...user, avatarUrl: result.avatarUrl });
            showSuccess('Avatar updated successfully!');
        } catch (err) {
            console.error('Failed to upload avatar:', err);
            showError(err.response?.data?.message || 'Failed to upload avatar.');
        }
    };

    const handleAvatarDelete = async () => {
        try {
            await UserService.deleteAvatar();
            setUser({ ...user, avatarUrl: null });
        } catch (err) {
            console.error('Failed to delete avatar:', err);
        }
    };

    const handleOpenNameDialog = () => {
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setIsNameDialogOpen(true);
        handleMenuClose(); // Close menu when opening dialog
    };

    const handleCloseNameDialog = () => {
        setIsNameDialogOpen(false);
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        try {
            await UserService.updateProfile(firstName, lastName);
            setUser({ ...user, firstName, lastName });
            handleCloseNameDialog();
        } catch (err) {
            console.error('Failed to update profile:', err);
            // Optionally dispatch error
        }
    };

    const isAuthPage = location.pathname.startsWith("/auth") || location.pathname.startsWith("/signup");

    // Determine if Subscription Block Modal should be shown
    const showSubscriptionModal = React.useMemo(() => {
        if (loadingUser || !user || isAuthPage || isSettingUp) return false;
        // Assume students don't need subscriptions for now, or handled by institution
        if (user.role === 'admin' || user.role === 'student') return false;

        const allowedStatuses = ['active', 'trialing'];
        const status = user.subscriptionStatus || 'incomplete';

        // Immediate failure for incomplete/past_due
        let show = !allowedStatuses.includes(status);

        // Allow access for canceled subscriptions if period is still active
        if (status === 'canceled') {
            const periodEnd = user.usageStats?.currentSubscription?.periodEnd;
            if (periodEnd && new Date(periodEnd) > new Date()) {
                show = false;
            }
        }

        return show;
    }, [user, loadingUser, isAuthPage, isSettingUp]);

    // Polling logic for post-checkout
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        // Check for 'checkout_success=true'
        if (searchParams.get('checkout_success') === 'true' && user) {
            // Already active, let Dashboard handle the welcome message
            if (['active', 'trialing'].includes(user.subscriptionStatus)) {
                return;
            }

            setIsSettingUp(true);
            let attempts = 0;
            const maxAttempts = 30; // 60 seconds (2s interval)

            const intervalId = setInterval(async () => {
                attempts++;
                try {
                    console.log(`[Polling] Checking subscription status... Attempt ${attempts}`);
                    const profile = await UserService.getProfile();

                    if (['active', 'trialing'].includes(profile.subscriptionStatus)) {
                        console.log("[Polling] Subscription active!");
                        setUser(profile);
                        setIsSettingUp(false);
                        clearInterval(intervalId);
                        showSuccess("Subscription activated successfully!");
                    } else if (attempts >= maxAttempts) {
                        console.log("[Polling] Timed out.");
                        setIsSettingUp(false);
                        clearInterval(intervalId);
                        navigate(location.pathname, { replace: true }); // On failure, clear param to avoid loops
                        showError("Subscription verification timed out. Please refresh or contact support.");
                    }
                } catch (err) {
                    console.error("[Polling] Error fetching profile:", err);
                }
            }, 2000);

            return () => clearInterval(intervalId);
        }
    }, [location.search, location.pathname, user, navigate, showSuccess, showError]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                <GlobalNotificationBanner />
                <WebSocketErrorHandler />
                <ImpersonationBanner />

                {/* Subscription Blocking Modal */}
                {user && (
                    <SubscriptionBlockModal
                        open={showSubscriptionModal}
                        status={user.subscriptionStatus}
                        tier={user.subscriptionTier}
                    />
                )}

                <AppBar position="static" color="default" elevation={1} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <Toolbar>
                        {/* Branding - Clickable */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mr: 1,
                                cursor: 'pointer',
                                '&:hover svg, &:hover .MuiTypography-root': {
                                    filter: (theme) => `drop-shadow(0 0 6px ${alpha(theme.palette.primary.main, 0.4)})`
                                },
                                '& svg, & .MuiTypography-root': {
                                    transition: 'filter 0.2s ease-in-out'
                                }
                            }}
                            onClick={() => navigate('/')}
                        >
                            <Logo style={{ height: 32, width: 32, marginRight: 8 }} />
                            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold', letterSpacing: '-0.5px', color: 'primary.main', mr: 1 }}>
                                Assignify
                            </Typography>
                            {location.pathname !== '/' && <ChevronRightIcon fontSize="small" color="action" />}
                        </Box>
                        {!isAuthPage && (
                            <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} aria-label="breadcrumb" sx={{ flexGrow: 1 }}>
                                {breadcrumbs.map((crumb, index) => {
                                    // Skip rendering "Dashboard" label if it's the first item, as "Assignify" serves that purpose visually
                                    if (crumb.label === 'Dashboard' && index === 0) return null;

                                    const last = index === breadcrumbs.length - 1;
                                    return last ? (
                                        <Typography key={crumb.path || index} color="text.primary" sx={{ typography: 'h6', fontWeight: 500 }}>
                                            {crumb.label}
                                        </Typography>
                                    ) : (
                                        <Link
                                            key={crumb.path || index}
                                            component={RouterLink}
                                            to={crumb.path}
                                            color="inherit"
                                            underline="hover"
                                            sx={{ typography: 'h6' }}
                                        >
                                            {crumb.label}
                                        </Link>
                                    );
                                })}
                            </Breadcrumbs>
                        )}
                        {/* Spacer if no breadcrumbs (e.g. auth page) to push theme toggle to right */}
                        {(isAuthPage || breadcrumbs.length === 0) && <Box sx={{ flexGrow: 1 }} />}

                        <IconButton onClick={toggleTheme} color="inherit">
                            {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>
                        {!isAuthPage && (
                            loadingUser ? (
                                <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : user ? (
                                <>
                                    <Tooltip title="Account settings">
                                        <IconButton
                                            onClick={handleMenuClick}
                                            size="small"
                                            sx={{ ml: 2 }}
                                            aria-controls={open ? 'account-menu' : undefined}
                                            aria-haspopup="true"
                                            aria-expanded={open ? 'true' : undefined}
                                        >
                                            <UserAvatar
                                                sx={{ width: 32, height: 32, fontSize: '1rem' }}
                                                src={user.avatarUrl}
                                                name={`${user.firstName} ${user.lastName}`}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        anchorEl={anchorEl}
                                        id="account-menu"
                                        open={open}
                                        onClose={handleMenuClose}
                                        onClick={handleMenuClose}
                                        PaperProps={{
                                            elevation: 0,
                                            sx: {
                                                overflow: 'visible',
                                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                                mt: 1.5,
                                                '&:before': {
                                                    content: '""',
                                                    display: 'block',
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 14,
                                                    width: 10,
                                                    height: 10,
                                                    bgcolor: 'background.paper',
                                                    transform: 'translateY(-50%) rotate(45deg)',
                                                    zIndex: 0,
                                                },
                                            },
                                        }}
                                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                    >
                                        <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 260 }}>
                                            {/* Avatar Area */}
                                            <Box sx={{ position: 'relative', mb: 2 }}>
                                                <UserAvatar
                                                    name={`${user.firstName} ${user.lastName}`}
                                                    src={user.avatarUrl}
                                                    sx={{ width: 120, height: 120, fontSize: '3rem' }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    component="label"
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: -5,
                                                        right: -5,
                                                        bgcolor: 'background.paper',
                                                        boxShadow: 1,
                                                        '&:hover': { bgcolor: 'background.default' }
                                                    }}
                                                >
                                                    <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
                                                    <PhotoCamera fontSize="small" color="primary" />
                                                </IconButton>
                                                {user.avatarUrl && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); handleAvatarDelete(); }}
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: -5,
                                                            left: -5,
                                                            bgcolor: 'background.paper',
                                                            boxShadow: 1,
                                                            '&:hover': { bgcolor: 'background.default' }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {/* User Info Area */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {user.firstName} {user.lastName}
                                                </Typography>
                                                {user.role !== 'student' && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); handleOpenNameDialog(); }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {user.email}
                                            </Typography>
                                        </Box>

                                        <Divider />

                                        {/* Actions */}
                                        {user.role !== 'student' && (
                                            <MenuItem onClick={() => { navigate('/account'); handleMenuClose(); }}>
                                                <ListItemIcon>
                                                    <AccountCircleIcon fontSize="small" />
                                                </ListItemIcon>
                                                Manage Account
                                            </MenuItem>
                                        )}
                                        <MenuItem onClick={onLogout}>
                                            <ListItemIcon>
                                                <LogoutIcon fontSize="small" />
                                            </ListItemIcon>
                                            Logout
                                        </MenuItem>
                                    </Menu>
                                </>
                            ) : null
                        )}
                    </Toolbar>
                </AppBar>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >

                    <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                        <ErrorBoundary>
                            <Outlet />
                        </ErrorBoundary>
                    </Box>
                </Box>

                {/* Edit Name Dialog */}
                <Dialog open={isNameDialogOpen} onClose={handleCloseNameDialog} fullWidth maxWidth="xs">
                    <form onSubmit={handleProfileUpdate}>
                        <DialogTitle>Edit Name</DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ mb: 2 }}>
                                Update your first and last name.
                            </DialogContentText>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="firstName"
                                label="First Name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                margin="dense"
                                id="lastName"
                                label="Last Name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                            />
                        </DialogContent>
                        <DialogActions sx={{ p: '16px 24px' }}>
                            <Button onClick={handleCloseNameDialog}>Cancel</Button>
                            <Button type="submit" variant="contained">Save Changes</Button>
                        </DialogActions>
                    </form>
                </Dialog>
                {/* Post-Checkout Loading Screen (UI/UX Pro Design) */}
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.drawer + 2,
                        backdropFilter: 'blur(12px)',
                        backgroundColor: mode === 'dark' ? 'rgba(10, 10, 15, 0.85)' : 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3
                    }}
                    open={isSettingUp}
                >
                    <CircularProgress
                        size={60}
                        thickness={2}
                        sx={{
                            color: mode === 'dark' ? '#fff' : 'primary.main',
                            '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                            },
                        }}
                    />
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="h5"
                            sx={{
                                color: mode === 'dark' ? '#fff' : 'text.primary',
                                fontWeight: 600,
                                letterSpacing: '-0.5px',
                                mb: 1,
                                textShadow: mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.5)' : 'none'
                            }}
                        >
                            Setting things up for you...
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                                fontWeight: 400
                            }}
                        >
                            Verifying your subscription
                        </Typography>
                    </Box>
                </Backdrop>
            </Box>
        </LocalizationProvider>
    );
}

function Layout() {
    return (
        <BreadcrumbProvider>
            <LayoutContent />
        </BreadcrumbProvider>
    );
}

export default Layout;